import { Image, Pressable, Text, View } from 'react-native';
import { CartaoApoio } from '../components/Recolhivel';
import type { Anexo, Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps } from './tipos';
import { dataBr, din, junta, num, op, pct, soma } from './util';
import { C } from '../components/theme';
import { fonteImagem } from '../components/Anexos';
import { abrirAnexo } from '../lib/anexos';
import { semBloqueio } from '../auth/AuthContext';
import { today, horaTela } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { acaoRelatorioPdf, dadosPdf, porDataPdf, secaoPdf } from '../lib/pdfFicha';
import type { BlocoPdf } from '../lib/pdf';

const ETAPAS = [N_('Projeto e licenças'), N_('Demolição'), N_('Fundação e estrutura'), N_('Alvenaria'), N_('Elétrica'), N_('Hidráulica'), N_('Telhado'),
  N_('Gesso e forro'), N_('Revestimento e pisos'), N_('Pintura'), N_('Marcenaria'), N_('Vidros e esquadrias'), N_('Acabamento'), N_('Limpeza final'), N_('Outra')];
const ICONE_ETAPA: Record<string, string> = { 'Elétrica': '⚡', 'Hidráulica': '🚿', 'Pintura': '🎨', 'Marcenaria': '🪚', 'Fundação e estrutura': '🧱', 'Alvenaria': '🧱', 'Telhado': '🏠', 'Demolição': '🔨', 'Revestimento e pisos': '🟫', 'Limpeza final': '🧹' };
const ANTES = N_('Antes'), DEPOIS = N_('Depois');

const atrasada = (r: Reg) => !!r.c.fim && r.c.fim < today() && num(r.c.feito) < 100;

/** Avanço físico (média ponderada pelo orçado) e financeiro (gasto x orçado) da obra. */
export function fisicoFinanceiro(etapas: Reg[]) {
  const orcado = soma(etapas, 'orcado'), gasto = soma(etapas, 'gasto');
  const peso = (r: Reg) => (orcado > 0 ? num(r.c.orcado) : 1);
  const totalPeso = etapas.reduce((s, r) => s + peso(r), 0);
  const fisico = totalPeso ? Math.round(etapas.reduce((s, r) => s + peso(r) * Math.min(100, num(r.c.feito)), 0) / totalPeso) : 0;
  return { orcado, gasto, fisico, financeiro: pct(gasto, orcado), atrasadas: etapas.filter(atrasada).length };
}

function Barra({ v, cor }: { v: number; cor: string }) {
  return <View style={{ height: 8, borderRadius: 4, backgroundColor: C.card2, overflow: 'hidden' }}><View style={{ width: `${Math.min(100, v)}%`, height: 8, backgroundColor: cor }} /></View>;
}

function PainelEtapas({ ctx, lista }: PainelProps) {
  if (!lista.length) return null;
  const f = fisicoFinanceiro(lista);
  return (
    <CartaoApoio titulo={'📊 ' + t('Cronograma físico-financeiro')} chave={'obras:crono'} resumo={t('Físico (obra pronta): {p}%', { p: f.fisico })} gap={8}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Físico (obra pronta): {p}%', { p: f.fisico })}</Text>
      <Barra v={f.fisico} cor={ctx.cor} />
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Financeiro: {gasto} de {orcado} ({p}%)', { gasto: din(f.gasto, ctx.moeda), orcado: din(f.orcado, ctx.moeda), p: f.financeiro })}</Text>
      <Barra v={f.financeiro} cor={f.gasto > f.orcado && f.orcado > 0 ? C.danger : C.ok} />
      {f.financeiro > f.fisico + 10 && <Text style={{ color: C.warn, fontSize: 12 }}>⚠️ {t('O dinheiro gasto está à frente do andamento da obra.')}</Text>}
      {!!f.atrasadas && <Text style={{ color: C.danger, fontSize: 12, fontWeight: '700' }}>⏰ {t('{n} etapa(s) atrasada(s)', { n: f.atrasadas })}</Text>}
    </CartaoApoio>
  );
}

/** Galeria Antes e Depois com as fotos do diário. */
function Galeria({ ctx, lista }: PainelProps) {
  const fotos = (marca: string) => lista.flatMap(r => (r.anexos ?? []).filter(a => a.nome.startsWith(t(marca) + ' · ') && fonteImagem(a)).map(a => ({ a, data: r.c.data as string })));
  const antes = fotos(ANTES), depois = fotos(DEPOIS);
  if (!antes.length && !depois.length) return lista.length ? <Text style={{ color: C.mute, fontSize: 12 }}>{t('Marque as fotos do diário como Antes ou Depois para montar a galeria.')}</Text> : null;
  const Col = ({ titulo, l }: { titulo: string; l: { a: Anexo; data: string }[] }) => (
    <View style={{ flex: 1, gap: 6 }}>
      <Text style={{ color: C.sub, fontWeight: '700', fontSize: 12 }}>{titulo}</Text>
      {l.slice(0, 6).map((x, i) => (
        <Pressable key={i} onPress={() => semBloqueio(() => abrirAnexo(x.a)).catch(() => {})}>
          <Image source={{ uri: fonteImagem(x.a)! }} style={{ width: '100%', aspectRatio: 4 / 3, borderRadius: 10, backgroundColor: C.card2 }} />
          <Text style={{ color: C.mute, fontSize: 10 }}>{dataBr(x.data)}</Text>
        </Pressable>
      ))}
    </View>
  );
  return (
    <CartaoApoio titulo={'📸 ' + t('Antes e depois')} chave={'obras:antesdepois'} resumo={String(antes.length + depois.length)} gap={8}>
      <View style={{ flexDirection: 'row', gap: 10 }}><Col titulo={t(ANTES)} l={antes} /><Col titulo={t(DEPOIS)} l={depois} /></View>
    </CartaoApoio>
  );
}

const obra: Colecao = {
  id: 'obra', nome: N_('Obras'), um: N_('Obra'), icone: '🏗️',
  campos: [
    { k: 'nome', r: N_('Nome da obra'), t: 'texto', ph: N_('Ex.: Reforma da cozinha') },
    { k: 'endereco', r: N_('Endereço'), t: 'endereco' },
    { k: 'inicio', r: N_('Início'), t: 'data' },
    { k: 'fim', r: N_('Entrega prevista'), t: 'data', aviso: [7] },
    { k: 'orcamento', r: N_('Orçamento total'), t: 'dinheiro' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '',
  sub: (r, ctx) => { const f = fisicoFinanceiro(ctx.filhos(r.id, 'etapa')); return junta(r.c.endereco, t('{p}% pronta', { p: f.fisico }), f.gasto ? t('gasto {v}', { v: din(f.gasto, ctx.moeda) }) : ''); },
  data: 'fim', anexos: true,
  acoes: (r, ctx) => [acaoRelatorioPdf(() => relatorioObra(ctx, r))],   // 0.65 (B15)
  vazio: N_('Cadastre a obra e, dentro dela, as etapas com orçamento, o diário com fotos, os profissionais e as entregas de material.'),
};

const etapa: Colecao = {
  id: 'etapa', pai: 'obra', nome: N_('Etapas'), um: N_('Etapa'), icone: r => ICONE_ETAPA[r.c.tipo] ?? '🧰',
  campos: [
    { k: 'tipo', r: N_('Etapa'), t: 'opcao', op: ETAPAS },
    { k: 'nome', r: N_('Descrição'), t: 'texto', ph: N_('Ex.: Troca da fiação da sala') },
    { k: 'inicio', r: N_('Início'), t: 'data' },
    { k: 'fim', r: N_('Fim previsto'), t: 'data', aviso: [2] },
    { k: 'feito', r: N_('Concluído (%)'), t: 'numero', un: '%' },
    { k: 'orcado', r: N_('Orçado'), t: 'dinheiro' },
    { k: 'gasto', r: N_('Gasto até agora'), t: 'dinheiro' },
    { k: 'quem', r: N_('Profissional'), t: 'ref', ref: 'profissional' },
  ],
  titulo: r => junta(op(r.c.tipo), r.c.nome),
  sub: (r, ctx) => junta(t('{p}% feito', { p: Math.min(100, num(r.c.feito)) }), r.c.orcado !== '' && r.c.orcado != null && `${din(r.c.gasto, ctx.moeda)} / ${din(r.c.orcado, ctx.moeda)}`, atrasada(r) && '⏰ ' + t('atrasada')),
  data: 'inicio', ordem: (a, b) => String(a.c.inicio || '9').localeCompare(String(b.c.inicio || '9')),
  feito: r => num(r.c.feito) >= 100,
  agenda: r => ({ start: r.c.inicio || today(), end: r.c.fim && r.c.fim > (r.c.inicio || '') ? r.c.fim : null }),
  painel: PainelEtapas, anexos: true,
  novo: () => ({ feito: 0 }),
  ajustar: c => (num(c.feito) > 100 ? { ...c, feito: 100 } : c),
};

const diario: Colecao = {
  id: 'diario', pai: 'obra', nome: N_('Diário'), um: N_('Registro do diário'), icone: '📔',
  campos: [
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'texto', r: N_('O que foi feito'), t: 'longo' },
    { k: 'etapa', r: N_('Etapa'), t: 'ref', ref: 'etapa', refPai: true },
  ],
  titulo: r => dataBr(r.c.data), sub: r => String(r.c.texto || '').slice(0, 120),
  ordem: 'data-desc', anexos: true, marcas: [ANTES, DEPOIS], painel: Galeria,
  novo: () => ({ data: today() }),
  vazio: N_('Anote o dia a dia da obra com fotos. Marque as fotos como Antes ou Depois para ver a evolução.'),
};

const profissional: Colecao = {
  id: 'profissional', nome: N_('Profissionais'), um: N_('Profissional ou fornecedor'), icone: '👷',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto' },
    { k: 'servico', r: N_('Serviço'), t: 'texto', ph: N_('Ex.: Eletricista, loja de material') },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'email', r: N_('E-mail'), t: 'email' },
    { k: 'endereco', r: N_('Endereço'), t: 'endereco' },
    { k: 'valor', r: N_('Valor combinado'), t: 'dinheiro' },
    { k: 'nota', r: N_('Avaliação'), t: 'estrelas' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(r.c.servico, r.c.telefone, num(r.c.nota) ? '★'.repeat(num(r.c.nota)) : ''), ordem: 'titulo', anexos: true,
};

const entrega: Colecao = {
  id: 'entrega', check: 'recebido', pai: 'obra', nome: N_('Entregas'), um: N_('Entrega de material'), icone: r => (r.c.recebido ? '✅' : '🚚'),
  campos: [
    { k: 'material', r: N_('Material'), t: 'texto', ph: N_('Ex.: 40 sacos de cimento') },
    { k: 'fornecedor', r: N_('Fornecedor'), t: 'ref', ref: 'profissional' },
    { k: 'data', r: N_('Data da entrega'), t: 'data', aviso: [1, 0] },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'recebido', r: N_('Recebido'), t: 'sim' },
  ],
  titulo: r => r.c.material || '', sub: (r, ctx) => junta(horaTela(r.c.hora), r.c.valor !== '' && r.c.valor != null && din(r.c.valor, ctx.moeda), ctx.porId(r.c.fornecedor)?.c.nome),
  data: 'data', feito: r => !!r.c.recebido, anexos: true,
  agenda: r => ({ time: r.c.hora || null }),
  filtros: [[N_('A receber'), r => !r.c.recebido], [N_('Recebidas'), r => !!r.c.recebido]],
};

/** 0.65 (B15): relatório da obra — físico-financeiro, etapas, entregas de material, profissionais e fornecedores e o diário. */
function relatorioObra(ctx: Ctx, o: Reg) {
  const $ = (x: unknown) => din(x, ctx.moeda);
  const temV = (x: unknown) => x !== '' && x != null;
  const etapas = porDataPdf(ctx.filhos(o.id, 'etapa'), 'inicio');
  const entregas = porDataPdf(ctx.filhos(o.id, 'entrega'), 'data');
  const diario = porDataPdf(ctx.filhos(o.id, 'diario'), 'data');
  const nomeDe = (id: unknown) => String(ctx.porId(String(id ?? ''))?.c.nome ?? '');
  const f = fisicoFinanceiro(etapas);
  const ids = [...new Set([...etapas.map(r => r.c.quem), ...entregas.map(r => r.c.fornecedor)].filter(Boolean).map(String))];
  const profs = ids.map(id => ctx.porId(id)).filter((r): r is Reg => !!r).sort((a, b) => String(a.c.nome).localeCompare(String(b.c.nome)));
  const linhasEtapas = etapas.map(r => [junta(op(r.c.tipo), r.c.nome), dataBr(r.c.inicio), dataBr(r.c.fim) + (atrasada(r) ? ' (!)' : ''),
    `${Math.min(100, num(r.c.feito))}%`, temV(r.c.orcado) ? $(r.c.orcado) : '', temV(r.c.gasto) ? $(r.c.gasto) : '', nomeDe(r.c.quem)]);
  if (linhasEtapas.length) linhasEtapas.push([t('Total'), '', '', `${f.fisico}%`, $(f.orcado), $(f.gasto), '']);
  const linhasEntregas = entregas.map(r => [dataBr(r.c.data), r.c.material || '', nomeDe(r.c.fornecedor), temV(r.c.valor) ? $(r.c.valor) : '', r.c.recebido ? t('Sim') : t('Não')]);
  if (linhasEntregas.length) linhasEntregas.push([t('Total'), '', '', $(soma(entregas, 'valor')), '']);
  const b: BlocoPdf[] = [
    { t: 'titulo', texto: o.c.nome || t('Obra') },
    ...dadosPdf([[t('Endereço'), o.c.endereco], [t('Início'), dataBr(o.c.inicio)], [t('Entrega prevista'), dataBr(o.c.fim)],
      [t('Orçamento total'), temV(o.c.orcamento) ? $(o.c.orcamento) : ''], [t('Observações'), o.c.notas]]),
    ...(etapas.length ? [
      { t: 'sub' as const, texto: t('Cronograma físico-financeiro') },
      { t: 'par' as const, tam: 10, texto: [t('Físico (obra pronta): {p}%', { p: f.fisico }),
        t('Financeiro: {gasto} de {orcado} ({p}%)', { gasto: $(f.gasto), orcado: $(f.orcado), p: f.financeiro }),
        f.atrasadas ? t('{n} etapa(s) atrasada(s)', { n: f.atrasadas }) : ''].filter(Boolean).join('\n') },
    ] : []),
    ...secaoPdf(t('Etapas'), [t('Etapa'), t('Início'), t('Fim previsto'), t('Concluído (%)'), t('Orçado'), t('Gasto até agora'), t('Profissional')],
      linhasEtapas, [0.25, 0.11, 0.12, 0.1, 0.13, 0.13, 0.16]),
    ...secaoPdf(t('Entregas'), [t('Data'), t('Material'), t('Fornecedor'), t('Valor'), t('Recebido')], linhasEntregas, [0.13, 0.37, 0.22, 0.16, 0.12]),
    ...secaoPdf(t('Profissionais'), [t('Nome'), t('Serviço'), t('Telefone'), t('E-mail'), t('Valor combinado')],
      profs.map(r => [r.c.nome || '', r.c.servico || '', r.c.telefone || '', r.c.email || '', temV(r.c.valor) ? $(r.c.valor) : '']), [0.24, 0.22, 0.17, 0.22, 0.15]),
    ...secaoPdf(t('Diário'), [t('Data'), t('O que foi feito')], diario.map(r => [dataBr(r.c.data), String(r.c.texto || '')]), [0.15, 0.85]),
  ];
  return { nome: junta(t('Obra'), o.c.nome), blocos: b };
}

export const OBRAS: ModDef = { id: 'obras', colecoes: [obra, etapa, diario, entrega, profissional] };
